'use client'

import { useRef } from 'react'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { ConditionStep, Step } from '@/lib/commands/graph-serialize'
import { TemplateChipBar } from '../TemplateChipBar'

export function ConditionStepEditor({
  step,
  steps,
  priorStepIds,
  onChange,
}: {
  step: ConditionStep
  steps: Step[]
  priorStepIds: string[]
  onChange: (step: ConditionStep) => void
}) {
  const exprRef = useRef<HTMLTextAreaElement | null>(null)
  const targetSteps = steps.filter((candidate) => candidate.id !== step.id)

  const insertToken = (token: string) => {
    const textarea = exprRef.current
    if (!textarea) {
      onChange({ ...step, expr: `${step.expr}${token}` })
      return
    }
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    onChange({ ...step, expr: `${step.expr.slice(0, start)}${token}${step.expr.slice(end)}` })
    requestAnimationFrame(() => {
      textarea.focus()
      textarea.setSelectionRange(start + token.length, start + token.length)
    })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${step.id}-expr`}>Condition</Label>
        <TemplateChipBar stepIds={priorStepIds} onInsert={insertToken} />
        <Textarea
          ref={exprRef}
          id={`${step.id}-expr`}
          className="min-h-24 font-mono"
          value={step.expr}
          placeholder="${$step1.campaigns[0].spend_pct} > 0.9"
          onChange={(event) => onChange({ ...step, expr: event.target.value })}
        />
        <p className="text-xs text-atrium-black/60">Use template values to compare fields. The expression should return true or false.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <TargetSelect
          label="If true, go to step"
          value={step.then}
          steps={targetSteps}
          onValueChange={(then) => onChange({ ...step, then })}
        />
        <TargetSelect
          label="If false, go to step"
          value={step.else}
          steps={targetSteps}
          onValueChange={(elseTarget) => onChange({ ...step, else: elseTarget })}
        />
      </div>
    </div>
  )
}

function TargetSelect({
  label,
  value,
  steps,
  onValueChange,
}: {
  label: string
  value: string
  steps: Step[]
  onValueChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Pick a step" />
        </SelectTrigger>
        <SelectContent>
          {steps.map((step) => (
            <SelectItem key={step.id} value={step.id}>{step.id}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
